import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Mic,
  BarChart3,
  Zap,
  ShieldCheck,
  Headphones,
  Phone,
  MessageSquare,
  CalendarClock,
  Truck,
  User,
  Bot,
  Building2,
  BrainCircuit,
  BarChart,
  Globe,
} from "lucide-react";

export default function VoiceAgentsPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white overflow-hidden">
      {/* Navbar */}
      <header className="fixed top-0 left-0 right-0 bg-[#0A0A0F]/90 backdrop-blur-md border-b border-gray-800 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="font-bold text-2xl text-[#9333EA]">
                VOXI
              </Link>
              <nav className="hidden md:flex ml-10 space-x-8">
                <Link
                  to="/"
                  className="text-gray-300 hover:text-[#9333EA] transition-colors py-2"
                >
                  Home
                </Link>
                <Link
                  to="/voice-agents"
                  className="text-[#9333EA] hover:text-[#9333EA] transition-colors py-2"
                >
                  Voice AI
                </Link>
                <Link
                  to="/use-cases/lead-qualification"
                  className="text-gray-300 hover:text-[#9333EA] transition-colors py-2"
                >
                  Use Cases
                </Link>
                <Link
                  to="/"
                  className="text-gray-300 hover:text-[#9333EA] transition-colors py-2"
                >
                  Pricing
                </Link>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button
                  variant="ghost"
                  className="text-gray-300 hover:text-white hover:bg-[#9333EA]/20"
                >
                  Sign In
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-[#9333EA] hover:bg-[#7C3AED] text-white font-medium">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with glow effect */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#9333EA]/20 blur-[120px] pointer-events-none" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#9333EA]/20 text-[#9333EA] text-sm font-medium mb-2">
              <Sparkles className="h-4 w-4 mr-2" />
              AI-Powered Voice Agents
            </div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              <span className="text-[#9333EA]">Voice AI</span> That Sounds
              <span className="block">Human, Not Robotic</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Create lifelike AI voice agents that can handle calls, qualify
              leads, and support customers 24/7 without sounding like robots.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
              <Link to="/signup">
                <Button className="bg-[#9333EA] hover:bg-[#7C3AED] text-white font-medium px-8 py-6 text-lg w-full sm:w-auto">
                  Start Building
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/assistant-dashboard">
                <Button
                  variant="outline"
                  className="border-gray-700 text-white hover:bg-white/5 hover:text-[#9333EA] px-8 py-6 text-lg w-full sm:w-auto"
                >
                  Try Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-gradient-to-br from-[#2A2A3F]/80 to-[#1A1A2E]/80 p-2 rounded-2xl border border-[#3F3F5F]/30 backdrop-blur-sm shadow-xl">
            <div className="bg-[#0F0F1A]/80 rounded-xl p-6 overflow-hidden">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-[#9333EA]/20 flex items-center justify-center mr-3">
                    <Bot className="h-6 w-6 text-[#9333EA]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">
                      VOXI Sales Assistant
                    </h3>
                    <p className="text-sm text-gray-400">Active Call</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <div className="h-8 w-8 rounded-full bg-[#9333EA]/20 flex items-center justify-center">
                    <Phone className="h-4 w-4 text-[#9333EA]" />
                  </div>
                </div>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <div className="h-8 w-8 rounded-full bg-[#9333EA]/20 flex-shrink-0 flex items-center justify-center mr-3">
                    <Bot className="h-4 w-4 text-[#9333EA]" />
                  </div>
                  <div className="bg-[#1A1A2E] rounded-lg p-3 max-w-[80%]">
                    <p className="text-sm text-gray-300">
                      Hi there! I'm VOXI, your AI sales assistant. I noticed you
                      were interested in our enterprise analytics solution.
                      Could you tell me a bit about your current data
                      challenges?
                    </p>
                  </div>
                </div>
                <div className="flex items-start justify-end">
                  <div className="bg-[#9333EA]/10 rounded-lg p-3 max-w-[80%]">
                    <p className="text-sm text-gray-200">
                      Yes, we're currently struggling with processing customer
                      feedback at scale. We have thousands of support tickets
                      and survey responses that we can't analyze effectively.
                    </p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-[#9333EA]/10 flex-shrink-0 flex items-center justify-center ml-3">
                    <User className="h-4 w-4 text-[#9333EA]" />
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="h-8 w-8 rounded-full bg-[#9333EA]/20 flex-shrink-0 flex items-center justify-center mr-3">
                    <Bot className="h-4 w-4 text-[#9333EA]" />
                  </div>
                  <div className="bg-[#1A1A2E] rounded-lg p-3 max-w-[80%]">
                    <p className="text-sm text-gray-300">
                      I understand completely. Our sentiment analysis module can
                      process thousands of feedback entries in minutes,
                      categorizing them by topic and sentiment. Would you like
                      me to schedule a demo with one of our data specialists to
                      show you how this works with your actual data?
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 relative">
                <div className="h-12 w-12 absolute left-1/2 -translate-x-1/2 -bottom-20 bg-[#9333EA] rounded-full flex items-center justify-center glow-purple z-10">
                  <Mic className="h-6 w-6 text-white" />
                </div>
                <div className="h-1 w-full bg-[#1A1A2E] rounded-full overflow-hidden">
                  <div className="h-full w-3/4 bg-[#9333EA] rounded-full"></div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>00:42</span>
                  <span>01:15</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#9333EA]/10 blur-[120px] pointer-events-none" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="text-[#9333EA]">Advanced</span> Voice AI Features
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our AI voice agents leverage cutting-edge technology to deliver
              human-like conversations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 backdrop-blur-sm hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
              <div className="h-12 w-12 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mb-6">
                <BrainCircuit className="h-6 w-6 text-[#9333EA]" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">
                Natural Conversations
              </h3>
              <p className="text-gray-400">
                Our AI understands context, remembers previous interactions, and
                responds naturally to interruptions and questions.
              </p>
            </div>

            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 backdrop-blur-sm hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
              <div className="h-12 w-12 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mb-4">
                <Globe className="h-6 w-6 text-[#9333EA]" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">
                Multilingual Support
              </h3>
              <p className="text-gray-400">
                Communicate with customers in over 30 languages with
                natural-sounding voices and accurate translations.
              </p>
            </div>

            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 backdrop-blur-sm hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
              <div className="h-12 w-12 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-[#9333EA]" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">
                Real-time Responses
              </h3>
              <p className="text-gray-400">
                Our AI responds in milliseconds with human-like latency,
                creating natural conversation flow without awkward pauses.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 backdrop-blur-sm hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
              <div className="flex items-center mb-6">
                <div className="h-10 w-10 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mr-4">
                  <BarChart className="h-5 w-5 text-[#9333EA]" />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  Real-time Analytics
                </h3>
              </div>
              <p className="text-gray-400 mb-6">
                Get instant insights from every conversation with sentiment
                analysis, intent detection, and conversion tracking.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-[#9333EA]/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#9333EA"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-300">
                    Real-time sentiment analysis
                  </span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-[#9333EA]/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#9333EA"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-300">
                    Conversion tracking
                  </span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-[#9333EA]/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#9333EA"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-300">
                    Comprehensive dashboards
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 backdrop-blur-sm hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
              <div className="flex items-center mb-6">
                <div className="h-10 w-10 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mr-4">
                  <ShieldCheck className="h-5 w-5 text-[#9333EA]" />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  Enterprise Security
                </h3>
              </div>
              <p className="text-gray-400 mb-6">
                Enterprise-grade security with end-to-end encryption, SOC 2
                compliance, and customizable data retention policies.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-[#9333EA]/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#9333EA"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-300">
                    End-to-end encryption
                  </span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-[#9333EA]/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#9333EA"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-300">
                    SOC 2 compliance
                  </span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-[#9333EA]/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#9333EA"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-300">
                    Custom data retention
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0D0D15]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#9333EA]/20 text-[#9333EA] text-sm font-medium mb-4">
              <Bot className="h-4 w-4 mr-2" />
              Voice AI Use Cases
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              How <span className="text-[#9333EA]">VOXI</span> Transforms Your
              Business
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our AI voice agents can be customized for various business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link to="/use-cases/lead-qualification" className="group">
              <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 h-full hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mr-4">
                    <Phone className="h-6 w-6 text-[#9333EA]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    Lead Qualification
                  </h3>
                </div>
                <p className="text-gray-400 mb-6">
                  Automatically qualify leads 24/7 with natural voice
                  conversations that identify high-value prospects and book
                  meetings.
                </p>
                <div className="flex items-center text-[#9333EA] group-hover:translate-x-1 transition-transform">
                  <span className="font-medium">Learn more</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </div>
            </Link>

            <Link to="/use-cases/customer-support" className="group">
              <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 h-full hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mr-4">
                    <Headphones className="h-6 w-6 text-[#9333EA]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    Customer Support
                  </h3>
                </div>
                <p className="text-gray-400 mb-6">
                  Provide 24/7 customer support with AI agents that can resolve
                  issues, answer questions, and escalate complex cases when
                  needed.
                </p>
                <div className="flex items-center text-[#9333EA] group-hover:translate-x-1 transition-transform">
                  <span className="font-medium">Learn more</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </div>
            </Link>

            <Link to="/use-cases/receptionists" className="group">
              <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 h-full hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mr-4">
                    <CalendarClock className="h-6 w-6 text-[#9333EA]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    Virtual Receptionists
                  </h3>
                </div>
                <p className="text-gray-400 mb-6">
                  Never miss a call with AI receptionists that handle
                  appointment scheduling, answer FAQs, and route calls to the
                  right department.
                </p>
                <div className="flex items-center text-[#9333EA] group-hover:translate-x-1 transition-transform">
                  <span className="font-medium">Learn more</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </div>
            </Link>

            <Link to="/use-cases/dispatch-service" className="group">
              <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 h-full hover:border-[#9333EA]/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(147,51,234,0.1)]">
                <div className="flex items-center mb-6">
                  <div className="h-12 w-12 bg-[#9333EA]/10 rounded-lg flex items-center justify-center mr-4">
                    <Truck className="h-6 w-6 text-[#9333EA]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    Dispatch Service
                  </h3>
                </div>
                <p className="text-gray-400 mb-6">
                  Optimize service operations with AI agents that handle
                  dispatch requests, schedule appointments, and provide
                  real-time updates.
                </p>
                <div className="flex items-center text-[#9333EA] group-hover:translate-x-1 transition-transform">
                  <span className="font-medium">Learn more</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-[500px] bg-[#9333EA]/30 glow-line-vertical" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-16">
            <span className="text-[#9333EA]">How</span> Voice AI Works
          </h2>

          <div className="relative z-10">
            <div className="flex justify-center mb-20">
              <div className="h-16 w-16 rounded-full bg-[#9333EA] flex items-center justify-center glow-purple">
                <Mic className="h-8 w-8 text-white" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative">
              <div className="relative">
                <div className="absolute top-0 right-0 md:left-1/2 md:-translate-x-1/2 w-px h-full md:w-full md:h-px bg-[#3F3F5F]/50 -z-10 md:top-8 md:bottom-auto" />
                <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-6 relative">
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#9333EA]/20 flex items-center justify-center text-[#9333EA] font-bold">
                    1
                  </div>
                  <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                    Voice Recognition
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Advanced AI processes natural speech in real-time with high
                    accuracy
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute top-0 right-0 md:left-1/2 md:-translate-x-1/2 w-px h-full md:w-full md:h-px bg-[#3F3F5F]/50 -z-10 md:top-8 md:bottom-auto" />
                <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-6 relative">
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#9333EA]/20 flex items-center justify-center text-[#9333EA] font-bold">
                    2
                  </div>
                  <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                    AI Processing
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Our AI understands context, intent, and sentiment to
                    generate appropriate responses
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute top-0 right-0 md:left-1/2 md:-translate-x-1/2 w-px h-full md:w-full md:h-px bg-[#3F3F5F]/50 -z-10 md:top-8 md:bottom-auto" />
                <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-6 relative">
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#9333EA]/20 flex items-center justify-center text-[#9333EA] font-bold">
                    3
                  </div>
                  <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                    Natural Response
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Human-like voice responses with natural pauses, intonation
                    and conversational flow
                  </p>
                </div>
              </div>
            </div>

            <div className="h-20 w-[1px] bg-[#3F3F5F]/50 mx-auto my-2" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-6 relative order-2 md:order-1">
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#9333EA]/20 flex items-center justify-center text-[#9333EA] font-bold">
                  4
                </div>
                <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                  Business Integration
                </h3>
                <p className="text-gray-400 text-sm">
                  Seamlessly connects with your CRM, calendar, and business
                  systems to take actions
                </p>
              </div>

              <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-6 relative order-1 md:order-2">
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#9333EA]/20 flex items-center justify-center text-[#9333EA] font-bold">
                  5
                </div>
                <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                  Analytics & Learning
                </h3>
                <p className="text-gray-400 text-sm">
                  Continuous improvement through conversation analysis and
                  machine learning
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0D0D15]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#9333EA]/20 text-[#9333EA] text-sm font-medium mb-4">
              Testimonials
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              What They Say About <span className="text-[#9333EA]">VOXI</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Trusted by businesses of all sizes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 hover:border-[#9333EA]/50 transition-all duration-300">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&q=80"
                  alt="Customer"
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#9333EA]/30"
                />
                <div className="ml-4">
                  <p className="font-bold text-white">Michael Chen</p>
                  <p className="text-sm text-gray-400">CTO, TechNova</p>
                </div>
              </div>
              <p className="text-gray-300 italic">
                "VOXI has transformed our lead qualification process. We've
                increased our sales team efficiency by 40% and our conversion
                rates have improved dramatically."
              </p>
            </div>

            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 hover:border-[#9333EA]/50 transition-all duration-300">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&q=80"
                  alt="Customer"
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#9333EA]/30"
                />
                <div className="ml-4">
                  <p className="font-bold text-white">Sarah Johnson</p>
                  <p className="text-sm text-gray-400">
                    Customer Success, Elevate
                  </p>
                </div>
              </div>
              <p className="text-gray-300 italic">
                "Our support team was overwhelmed with calls. VOXI now handles
                65% of our support volume, and our customers love how natural
                the conversations feel."
              </p>
            </div>

            <div className="bg-[#1A1A2E]/80 border border-[#3F3F5F]/30 rounded-xl p-8 hover:border-[#9333EA]/50 transition-all duration-300">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&q=80"
                  alt="Customer"
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#9333EA]/30"
                />
                <div className="ml-4">
                  <p className="font-bold text-white">David Wilson</p>
                  <p className="text-sm text-gray-400">CEO, MedServe</p>
                </div>
              </div>
              <p className="text-gray-300 italic">
                "Our virtual receptionist handles all our appointment scheduling
                and patient inquiries. We've reduced no-shows by 35% and our
                staff can focus on patient care."
              </p>
            </div>
          </div>

          <div className="mt-16 flex flex-wrap justify-center gap-12 items-center opacity-70">
            <div className="text-white/50 font-bold text-xl">Amazon</div>
            <div className="text-white/50 font-bold text-xl">Microsoft</div>
            <div className="text-white/50 font-bold text-xl">Salesforce</div>
            <div className="text-white/50 font-bold text-xl">Shopify</div>
            <div className="text-white/50 font-bold text-xl">Slack</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-[#9333EA]/10 blur-[120px] pointer-events-none" />
        </div>
        <div className="max-w-5xl mx-auto relative">
          <div className="bg-gradient-to-r from-[#2A2A3F]/80 to-[#1A1A2E]/80 p-1 rounded-2xl">
            <div className="bg-[#0F0F1A]/80 rounded-xl p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Ready to transform your business with{" "}
                <span className="text-[#9333EA]">VOXI</span>?
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
                Start building your AI voice agent today and see the results for
                yourself.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/signup">
                  <Button className="bg-[#9333EA] hover:bg-[#7E22CE] text-white font-medium px-8 py-6 text-lg w-full sm:w-auto">
                    Start Building
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button
                    variant="outline"
                    className="border-[#3F3F5F] text-white hover:bg-white/5 hover:text-[#9333EA] px-8 py-6 text-lg w-full sm:w-auto"
                  >
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1F1F2E] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-medium text-[#9333EA] mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Features
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Integrations
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-[#9333EA] mb-4">Use Cases</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/use-cases/lead-qualification"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Lead Qualification
                  </Link>
                </li>
                <li>
                  <Link
                    to="/use-cases/customer-support"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Customer Support
                  </Link>
                </li>
                <li>
                  <Link
                    to="/use-cases/receptionists"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Receptionists
                  </Link>
                </li>
                <li>
                  <Link
                    to="/use-cases/dispatch-service"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Dispatch Service
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-[#9333EA] mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-[#9333EA] mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-[#9333EA] transition-colors"
                  >
                    Terms
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-[#1F1F2E] flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <Link to="/" className="font-bold text-xl text-[#9333EA]">
                VOXI
              </Link>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Tempo Labs. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Custom CSS for glow effects */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .glow-purple {
          box-shadow: 0 0 20px 2px rgba(147, 51, 234, 0.3);
        }
        .glow-line-vertical {
          box-shadow: 0 0 15px 1px rgba(147, 51, 234, 0.4);
        }
      `,
        }}
      />
    </div>
  );
}
